import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { IOSAppAnnouncementModal } from '../components/ui/AnnouncementModal';
import { NAV_ITEMS, UTILITY_ITEMS, CREATE_ITEM } from './navItems';
import { buildBreadcrumb } from './breadcrumb';
import { TOPBAR_ACTIONS_SLOT_ID } from './TopbarActions';
import { primeSupervisionStatus, useSupervisionStatus } from '../features/parentPortal/supervisionStatus';
import graspdLogo from '../assets/graspd-logo.svg';
import '../styles/newsletter-signup.css';
import '../styles/shell.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/graspd/id6759442342';


/**
 * Layout with a persistent ChatGPT-like sidebar pinned to the far left
 * and a sticky top banner brand. No rainbow effects.
 */
export default function Layout({ children }) {
  const { pathname } = useLocation();

  const [courseTitle, setCourseTitle] = useState(() => {
    try { return sessionStorage.getItem('ui.currentCourseTitle') || ''; } catch { return ''; }
  });
  const crumbs = useMemo(() => buildBreadcrumb(pathname, courseTitle), [pathname, courseTitle]);
  useEffect(() => {
    try { setCourseTitle(sessionStorage.getItem('ui.currentCourseTitle') || ''); } catch { }
  }, [pathname]);

  useEffect(() => {
    function onTitleUpdate(e) {
      if (e?.detail?.title) setCourseTitle(e.detail.title);
    }
    window.addEventListener('graspd:setCourseTitle', onTitleUpdate);
    return () => window.removeEventListener('graspd:setCourseTitle', onTitleUpdate);
  }, []);

  const { user, enabled } = useAuth();

  // Prefer any available provider photo, fall back to Firebase user.photoURL
  const primaryPhotoURL = (user?.photoURL || user?.providerData?.[0]?.photoURL) || null;
  // Track image load errors to gracefully fall back to initials
  const [photoErrorKey, setPhotoErrorKey] = useState(0);

  useEffect(() => {
    // reset error flag whenever the photo source changes (e.g., user switches)
    setPhotoErrorKey(0);
  }, [primaryPhotoURL]);
  const initials = (name) =>
    String(name || '')
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase())
      .slice(0, 2)
      .join('') || 'U';

  const isActive = (to) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  // The Parents destination is the supervisor surface, which most learners
  // never use — it only earns a permanent slot once the account actually
  // supervises someone. Supervisors who haven't linked anyone yet reach it
  // from Settings (see SupervisorsSettingsPage), so nobody is stranded.
  const { supervises } = useSupervisionStatus();
  useEffect(() => { primeSupervisionStatus(user?.uid || null); }, [user?.uid]);

  const navItems = NAV_ITEMS.filter((item) => {
    if (item.to !== '/family') return true;
    // Keep it while the user is standing on it — a nav item vanishing out
    // from under the page you are looking at (e.g. right after unlinking
    // your last student) reads as a bug.
    return supervises || isActive('/family');
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAppStoreModal, setShowAppStoreModal] = useState(true);
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  // Close the drawer on any route change so a tapped destination never
  // leaves it open.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  // While open: move focus in, trap Tab inside the panel, and let Escape
  // close it and hand focus back to the button that opened it.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const panel = drawerRef.current;
    const getFocusable = () =>
      panel ? Array.from(panel.querySelectorAll('a[href], button:not([disabled])')) : [];
    getFocusable()[0]?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // Check if we are on a page that should hide the sidebar
  const barePath = pathname.replace(/\/+$/, '') || '/';
  // `/` is the enterprise homepage. `/academy` is the consumer splash.
  // `/enterprise` only redirects to `/`.
  const isMarketingPage = barePath === '/' || barePath === '/academy' || barePath === '/enterprise';
  const isPricingPage = pathname === '/pricing';
  const isLoginPage = pathname === '/login';
  // /help is intentionally excluded — it's a normal in-app page (the
  // sidebar's own Help destination), not a standalone legal page.
  const isLegalPage = pathname === '/privacy' || pathname === '/terms';

  // Hide sidebar on marketing pages, pricing, legal pages, or on login when signed out
  const hideChrome = isMarketingPage || isPricingPage || isLegalPage || (isLoginPage && !user);

  if (hideChrome) {
    return (
      <div className="layout-root">
        <main className="w-full">
          {children}
        </main>
      </div>
    );
  }

  // Shared by the desktop sidebar and the mobile drawer so there is exactly
  // one nav definition. `onNavigate` (only passed by the drawer) closes it
  // after a destination is chosen.
  const renderSidebarBody = (onNavigate) => (
    <>
      <Link to="/dashboard" className="sidebar-workspace" aria-label="Go to dashboard" onClick={onNavigate}>
        <img src={graspdLogo} alt="Graspd" className="sidebar-workspace-logo" />
      </Link>

      <Link to={CREATE_ITEM.to} className="sidebar-create" aria-label={CREATE_ITEM.label} onClick={onNavigate}>
        {CREATE_ITEM.icon}
        <span>{CREATE_ITEM.title}</span>
      </Link>

      <nav className="sidebar-nav" aria-label="Primary">
        {navItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`sidebar-item ${isActive(item.to) ? 'is-active' : ''}`}
            aria-label={item.label}
            aria-current={isActive(item.to) ? 'page' : undefined}
            onClick={onNavigate}
          >
            {item.icon}
            <span>{item.title}</span>
          </Link>
        ))}
      </nav>

      <div className="sidebar-spacer" />

      <div className="sidebar-footer">
        {UTILITY_ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`sidebar-item ${isActive(item.to) ? 'is-active' : ''}`}
            aria-label={item.label}
            aria-current={isActive(item.to) ? 'page' : undefined}
            onClick={onNavigate}
          >
            {item.icon}
            <span>{item.title}</span>
          </Link>
        ))}

        <button type="button" className="sidebar-item" onClick={() => { handleAppStoreClick(); onNavigate?.(); }} aria-label="Download iOS App">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          <span>Get iOS app</span>
        </button>

        <Link to="/settings" className="sidebar-user" aria-label="Account" onClick={onNavigate}>
          <span className="sidebar-avatar">
            {primaryPhotoURL && photoErrorKey === 0 ? (
              <img src={primaryPhotoURL} alt="" referrerPolicy="no-referrer" onError={() => setPhotoErrorKey(1)} />
            ) : (
              initials(user?.displayName || user?.email || 'U')
            )}
          </span>
          <span className="sidebar-user-text">
            <span className="sidebar-user-name">{user?.displayName || 'Account'}</span>
            <span className="sidebar-user-role">Learner</span>
          </span>
        </Link>
      </div>
    </>
  );

  return (
    <div className="layout-root">
      {/* Top banner */}
      <header className="layout-topbar" inert={drawerOpen || undefined}>
        <button
          ref={menuButtonRef}
          type="button"
          className="topbar-menu-btn"
          aria-label="Open navigation"
          aria-expanded={drawerOpen ? 'true' : 'false'}
          onClick={() => setDrawerOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        <div className="topbar-crumbs" aria-live="polite">
          {crumbs.map((crumb, i) => (
            <React.Fragment key={crumb.text}>
              {i > 0 && <span className="topbar-crumb-sep" aria-hidden="true">/</span>}
              <span className={crumb.muted ? 'topbar-crumb is-muted' : 'topbar-crumb'}>{crumb.text}</span>
            </React.Fragment>
          ))}
        </div>

        {/* Right-hand action slot. Pages fill it with <TopbarActions> — see
            TopbarActions.jsx. Always rendered so the portal target exists
            before any page mounts. */}
        <div className="topbar-actions" id={TOPBAR_ACTIONS_SLOT_ID} />

        {/* Mobile profile button (top right) */}
        <div className="topbar-right-mobile">
          <Link to="/settings" aria-label="Account">
            <button className="mobile-profile-btn">
              {enabled && user ? (
                primaryPhotoURL && photoErrorKey === 0 ? (
                  <img
                    src={primaryPhotoURL}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={() => setPhotoErrorKey(1)}
                  />
                ) : (
                  initials(user.displayName || user.email || 'U')
                )
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="7.5" r="4" />
                  <path d="M4 20c0-3.5 4-6 8-6s8 2.5 8 6" />
                </svg>
              )}
            </button>
          </Link>
        </div>
      </header>

      {/* Fixed left rail (desktop only — hidden below the sidebar breakpoint) */}
      <aside className="sidebar-fixed" aria-label="Primary sidebar" inert={drawerOpen || undefined}>
        {renderSidebarBody()}
      </aside>

      {/* Main content area with left padding to clear the fixed rail.
          Marked inert while the mobile drawer is open so a screen reader's
          browse-mode cursor can't wander into the page behind the scrim —
          the drawer itself (below) is never given this attribute. */}
      <div className="main-outer" inert={drawerOpen || undefined}>
        <main className="main container">
          {children}
        </main>
      </div>

      {/* Mobile nav drawer — reuses the desktop sidebar's own markup so
          there is exactly one nav definition. Only mounted while open. */}
      {drawerOpen && (
        <>
          <div className="drawer-scrim" onClick={() => setDrawerOpen(false)} />
          <aside className="drawer-panel" role="dialog" aria-modal="true" aria-label="Navigation" ref={drawerRef}>
            {renderSidebarBody(() => setDrawerOpen(false))}
          </aside>
        </>
      )}

      {/* iOS App Store Announcement Modal — signed-in users only */}
      {user && showAppStoreModal && (
        <IOSAppAnnouncementModal
          onDownload={handleAppStoreClick}
          onClose={() => setShowAppStoreModal(false)}
        />
      )}
    </div>
  );
}